import { useMemo } from 'react';
import { City, Country } from 'country-state-city';
import useTranslation from '@/hooks/useTranslation';

const EMPTY = [];
const COUNTRIES = Country.getAllCountries() || EMPTY;
const COUNTRY_ISO_BY_NAME = new Map(COUNTRIES.map((c) => [c.name, c.isoCode]));
const CITY_OPTIONS_CACHE = new Map();

const getRawCityOptions = (countryName) => {
    const name = String(countryName || '').trim();
    if (!name) return EMPTY;

    const iso = COUNTRY_ISO_BY_NAME.get(name);
    if (!iso) return EMPTY;

    if (CITY_OPTIONS_CACHE.has(iso)) return CITY_OPTIONS_CACHE.get(iso);

    const cities = Array.from(
        new Set((City.getCitiesOfCountry(iso) || []).map((c) => c?.name).filter(Boolean))
    ).map((cityName) => ({ value: cityName, label: cityName }));

    CITY_OPTIONS_CACHE.set(iso, cities);
    return cities;
};

/**
 * Returns translated country and city select options.
 *
 * @param {string} [selectedCountry] - English country name used to derive city list
 * @returns {{ countryOptions: Array, cityOptions: Array }}
 */
export default function useLocationFilter(selectedCountry) {
    const { t } = useTranslation();

    const countryOptions = useMemo(
        () => COUNTRIES.map((c) => ({ value: c.name, label: t(c.name) })),
        [t]
    );

    const rawCityOptions = useMemo(
        () => getRawCityOptions(selectedCountry),
        [selectedCountry]
    );

    const cityOptions = useMemo(
        () => rawCityOptions.map((opt) => ({ ...opt, label: t(opt.value) })),
        [rawCityOptions, t]
    );

    return { countryOptions, cityOptions };
}
